import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import {
  apiClient,
  type ApiResponse,
  UnitDetailsLeaseLoaderResult,
} from "~/services/apiClient";
import { _UnitPageTabType, EntityListData, LoaderData } from "~/types";
import { FormattedLeaseData, NewLeaseFormPayload } from "~/types/lease";
import { getAuthWrapper, INTENTS, loadCookiesData, resolveActorUserName , getReadyAuthUser } from "~/utils";
import { withEntityCacheInvalidation } from "~/utils/loaderQueryCache";
import * as APITypes from "~/API";

export const UnitDetailsLeaseLoader = async ({
  request,
  params,
}: LoaderFunctionArgs): Promise<UnitDetailsLeaseLoaderType | Response> => {
  const auth = getAuthWrapper();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) redirect("/explore");
  const { ownerID, ownerType } = authAccessInfo.data;

  const unitID = params.unitID || "";

  const result = await apiClient.loader<UnitDetailsLeaseLoaderResult>(
    "unit-details-lease",
    { id: unitID, ownerID, ownerType },
  );

  if (!result.success || !result.data) {
    console.error("Failed to load unit details lease:", result.error);
    throw new Error(result.error ?? "Failed to load unit details lease");
  }

  const data = result.data;
  const userName = await resolveActorUserName(user, authAccessInfo);

  // Backend must keep propertyList/buildingList empty for unit dropdowns;
  // getEntityListOptions prefers propertyList when non-empty (shows raw IDs).
  const rawMap = data.unitListMap as EntityListData;
  const unitListMap: EntityListData = {
    ...rawMap,
    propertyList: [],
    buildingList: [],
    unitList: rawMap.unitList ?? [],
    propertyMap: rawMap.propertyMap ?? {},
    buildingMap: rawMap.buildingMap ?? {},
    unitMap: rawMap.unitMap ?? {},
  };

  return {
    tab: "lease" as _UnitPageTabType,
    unitDetails: {
      success: true,
      data: data.unitDetails,
    } as LoaderData<APITypes.Unit>,
    ownerID,
    ownerType,
    userName,
    formattedLeaseList: data.formattedLeaseList as FormattedLeaseData[],
    tenantList: data.tenantList,
    unitListMap,
  };
};

/** Append Lambda `debugLogs` to `error` when present (matches building edit action). */
function withApiDebugTrail<T>(result: ApiResponse<T>): ApiResponse<T> {
  if (result.success || !result.debugLogs?.length) return result;
  return {
    ...result,
    error: `${result.error ?? "Request failed"} | ${result.debugLogs.join(" | ")}`,
  };
}

export type UnitDetailsLeaseLoaderType = {
  tab: _UnitPageTabType;
  unitDetails: LoaderData<APITypes.Unit>;
  ownerID: string;
  ownerType: APITypes.OwnerType;
  userName: string;
  formattedLeaseList: FormattedLeaseData[];
  tenantList: APITypes.Tenant[];
  // activeKey: string;
  unitListMap: EntityListData;
};

export const UnitDetailsLeaseAction = async ({
  request,
  params,
}: ActionFunctionArgs) => {
  const formData = await request.formData();
  const intent = formData.get("intent");
  const auth = getAuthWrapper();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;
  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  const unitID = params.unitID || "";
  const unitLeaseInvalidation = [
    { entity: "unit" as const, entityID: unitID },
    { entity: "lease" as const },
  ];
  switch (intent) {
    case INTENTS._delete_lease: {
      return withEntityCacheInvalidation(unitLeaseInvalidation, async () => {
        const bodyStr = formData.get("body") as string;
        const payload = bodyStr ? JSON.parse(bodyStr) : {};
        const leaseID = payload.id ?? payload.leaseID ?? bodyStr;
        const result = await apiClient.sendIntent(INTENTS._delete_lease, {
          id: leaseID,
          _version: payload._version,
          isAdminOrSuperAdmin,
        });
        return withApiDebugTrail(result);
      });
    }
    case INTENTS._terminate_lease: {
      return withEntityCacheInvalidation(unitLeaseInvalidation, async () => {
        const payload = JSON.parse(formData.get("body") as string) as {
          leaseID: string;
          endDate: string;
          status: APITypes.LeaseStatusType;
          _version?: number;
        };
        const result = await apiClient.sendIntent<APITypes.Lease>(
          INTENTS._terminate_lease,
          {
            id: payload.leaseID,
            leaseID: payload.leaseID,
            status: payload.status,
            endDate: payload.endDate,
            _version: payload._version,
            isAdminOrSuperAdmin,
          },
        );
        return withApiDebugTrail(result);
      });
    }
    case INTENTS._add_lease: {
      return withEntityCacheInvalidation(unitLeaseInvalidation, async () => {
        const payload = JSON.parse(
          formData.get("body") as string,
        ) as NewLeaseFormPayload;
        const leasePayload = {
          ...payload,
          entityID: unitID,
          ownerID,
          ownerType,
          isAdminOrSuperAdmin,
        };
        const result = await apiClient.sendIntent<APITypes.Lease>(
          INTENTS._add_lease,
          leasePayload,
        );
        if (result.success && result.data) {
          return { success: true, data: result.data };
        }
        const r = withApiDebugTrail(result);
        return {
          success: false,
          data: {} as APITypes.Lease,
          error: r.error ?? "Failed to create lease",
        };
      });
    }
  }
  return null;
};
